import React from 'react';
import axios from 'axios';
import { Button, Spin, message, Popconfirm } from 'antd';
import Draggable from 'react-draggable';
import { uploadVideoClaimSnapshot, endVideoClaimSession, joinVideoClaim } from '@service/videoClaim';
import './index.scss';

const INVITEE_TYPE_LABEL = {
    SUBJECT: '标的',
    THIRD_PARTY: '三者',
    OTHER: '其他',
};

// 坐席端：本地 Canvas 截图时的缩略图缩小展示（1s 后消失）
const makeSnapshotThumb = (dataUrl) => {
    try {
        const node = document.createElement('div');
        node.style.cssText = [
            'position:fixed', 'z-index:9999', 'top:50%', 'left:50%',
            'transform:translate(-50%,-50%)',
            'width:240px', 'height:160px',
            'background:#000', 'border:2px solid #fff',
            'border-radius:6px',
            'box-shadow:0 4px 20px rgba(0,0,0,0.35)',
            'overflow:hidden',
            'transition:opacity 0.25s ease, transform 0.25s ease',
        ].join(';');
        const img = document.createElement('img');
        img.src = dataUrl;
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
        node.appendChild(img);
        document.body.appendChild(node);
        // 1s 后淡出，0.25s 过渡完成后移除
        setTimeout(() => {
            node.style.opacity = '0';
            node.style.transform = 'translate(-50%,-50%) scale(0.6)';
        }, 1000);
        setTimeout(() => {
            if (node && node.parentNode) node.parentNode.removeChild(node);
        }, 1400);
    } catch (e) {}
};

// SDK 单独拆分到异步 chunk；只有经办真正接通视频时才下载，避免影响常规工单详情首屏。
const loadTrtcSdk = async () => {
    const module = await import(/* webpackChunkName: "trtc-sdk-v5" */ 'trtc-sdk-v5');
    return module.default || module;
};

// 尺寸档位（宽度 px），画面区高度按客户视频宽高比动态算出
const SIZE_STEPS = ['s', 'm', 'l'];
const SIZE_WIDTH = { s: 240, m: 320, l: 420 };
const SIZE_FRAME_HEIGHT = { s: 300, m: 400, l: 520 }; // 默认竖向高度（与 SCSS 一致），收到视频后按真实比例覆盖

/**
 * V3 视频理赔（新版）经办端：
 * - 坐席：不推视频流（纯音频 + 客户视频流 720P），保障音频优先
 * - 拍照：本地 Canvas 截取 客户视频流 帧（因为 TRTC 入门版无法使用 snapshotTrtcVideoFrame），上传到后端转存 picture 表
 * - 通话时长：接通后本地计时，结束时一并写入
 * - 结束会话：二次确认
 *
 * props.session 由后端序列化后的会话信息（{sessionId, flowId, inviteeName, inviteeType, inviteeRef,
 * agentToken, status, roomId, sdkAppId, agentUserId, agentUserSig, customerUserId, ...}）。
 */
class VideoSurveyRoom extends React.PureComponent {
    state = {
        loading: false,
        error: '',
        remoteReady: false,
        connected: false,  // 已在 TRTC 房间中且收到客户音视频
        durationSec: 0,
        snapshotting: false,
        size: 'm',        // 窗口尺寸档：s / m / l
        aspectRatio: null, // 客户视频真实宽高比（width/height），用于去黑边自适应
    };

    componentDidMount() {
        this.unmounted = false;
        this._durationTimer = null;
        this._previewHidden = null;
        this._entering = false;
        this._aspectTimer = null;
        this.connectSocket();
        this.autoEnterRoomIfReady();
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.session && prevProps.session.sessionId;
        const nextId = this.props.session && this.props.session.sessionId;
        const prevStatus = prevProps.session && prevProps.session.status;
        const nextStatus = this.props.session && this.props.session.status;
        if (prevId !== nextId) {
            // session 切换：全部重来
            this.cleanupTrtc();
            this.disconnectSocket();
            this.connectSocket();
            this.autoEnterRoomIfReady();
        } else if (prevId && prevId === nextId && prevStatus !== nextStatus) {
            // 同一 session 但 status 变化（如 INVITED → ACCEPTED）：需要进房
            if (!this.trtc) {
                this.connectSocket();
                this.autoEnterRoomIfReady();
            }
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.clearDurationTimer();
        this.clearAspectPoll();
        this.cleanupTrtc();
        this.disconnectSocket();
    }

    get sessionId() {
        return this.props.session && this.props.session.sessionId;
    }

    get session() {
        return this.props.session || {};
    }

    request = async (method, url, data) => {
        const response = await axios({ method, url, data });
        const result = response && response.data;
        if (!result || result.errCode !== 0) throw new Error(result && result.errInfo || '视频请求失败');
        return result.result;
    };

    isCurrentSession = (data = {}) => data.sessionId === this.sessionId;

    safeSetState = (state, callback) => {
        if (!this.unmounted) this.setState(state, callback);
    };

    clearDurationTimer = () => {
        if (this._durationTimer) {
            clearInterval(this._durationTimer);
            this._durationTimer = null;
        }
    };

    startDurationTimer = () => {
        this.clearDurationTimer();
        this._durationTimer = setInterval(() => {
            this.setState(prev => ({ durationSec: (prev.durationSec || 0) + 1 }));
        }, 1000);
    };

    // ============ 缩放：放大 / 恢复 / 缩小 ============
    setSize = (size) => {
        if (SIZE_STEPS.indexOf(size) === -1) return;
        this.setState({ size });
    };

    zoomIn = () => {
        const idx = SIZE_STEPS.indexOf(this.state.size);
        if (idx < SIZE_STEPS.length - 1) this.setSize(SIZE_STEPS[idx + 1]);
    };

    zoomOut = () => {
        const idx = SIZE_STEPS.indexOf(this.state.size);
        if (idx > 0) this.setSize(SIZE_STEPS[idx - 1]);
    };

    resetSize = () => this.setSize('m');

    // ============ 画面自适应：按客户视频真实宽高比去黑边 ============
    getRemoteVideoNode = () => {
        const container = document.getElementById('video-survey-remote');
        if (container && container.querySelectorAll) {
            const v = container.querySelector('video');
            if (v) return v;
        }
        const byId = document.getElementById('video-survey-remote');
        if (byId && byId.tagName === 'VIDEO') return byId;
        const all = document.querySelectorAll('video');
        for (let i = 0; i < all.length; i += 1) {
            const v = all[i];
            const p = v.parentNode && v.parentNode.id === 'video-survey-remote';
            const pp = v.parentNode && v.parentNode.parentNode && v.parentNode.parentNode.id === 'video-survey-remote';
            if ((p || pp) && v.srcObject) return v;
        }
        return null;
    };

    // 读取视频真实宽高，动态设置画面区高度，使容器比例贴合视频，配合 object-fit: cover 消除黑边
    syncAspectRatio = () => {
        const v = this.getRemoteVideoNode();
        const w = v && v.videoWidth;
        const h = v && v.videoHeight;
        if (!w || !h) return;
        const ratio = w / h;
        if (!ratio || ratio <= 0 || !isFinite(ratio)) return;
        // 仅当比例变化时更新，避免每帧 setState
        if (this.state.aspectRatio && Math.abs(this.state.aspectRatio - ratio) < 0.001) return;
        this.setState({ aspectRatio: ratio });
    };

    startAspectPoll = () => {
        this.clearAspectPoll();
        // TRTC 视频就绪前 videoWidth 可能为 0，轮询直到读到有效宽高
        this._aspectTimer = setInterval(this.syncAspectRatio, 300);
    };

    clearAspectPoll = () => {
        if (this._aspectTimer) {
            clearInterval(this._aspectTimer);
            this._aspectTimer = null;
        }
    };

    // ============ socket ============
    connectSocket = () => {
        if (!this.sessionId || !window.io) return;
        if (this.socket) return; // 已连接，防止重复创建
        this.socket = window.io('/video-survey', {
            transports: ['websocket'],
            forceNew: true,
            query: { accessToken: window.accessToken },
        });
        this.socket.on('calling', (data) => {
            if (!this.isCurrentSession(data)) return;
            // 客户呼叫中；等待接听流程由全局 VideoClaimIncoming 弹窗处理
            this.props.onSessionChanged && this.props.onSessionChanged(data);
        });
        this.socket.on('sessionAccepted', (data) => {
            if (!this.isCurrentSession(data)) return;
            this.props.onSessionChanged && this.props.onSessionChanged(data);
            this.autoEnterRoomIfReady(data);
        });
        this.socket.on('customerJoined', (data) => {
            if (!this.isCurrentSession(data)) return;
            this.safeSetState({ connected: true });
            this.props.onSessionChanged && this.props.onSessionChanged(data);
        });
        this.socket.on('snapshotUploaded', (data) => {
            if (!this.isCurrentSession(data)) return;
            if (data.status === 'success') message.success('截图已归档到“本地”');
            if (data.status === 'fail') message.error(data.errorMessage || '截图归档失败');
        });
        this.socket.on('recordingFailed', (data) => {
            if (!this.isCurrentSession(data)) return;
            this.cleanupTrtc();
            this.props.onSessionEnded && this.props.onSessionEnded(data);
            message.error(data.recordingError || '云端录制启动失败，视频无法留存');
        });
        this.socket.on('recordingReady', (data) => {
            if (this.isCurrentSession(data) && this.props.onSessionChanged) {
                this.props.onSessionChanged(data);
            }
        });
        this.socket.on('sessionEnded', (data) => {
            if (!this.isCurrentSession(data)) return;
            this.cleanupTrtc();
            this.props.onSessionChanged && this.props.onSessionChanged(data);
            this.props.onSessionEnded && this.props.onSessionEnded(data);
            message.info(data.endReason || '视频通话已结束');
        });
        // 客户侧拍照也同步提示一下
        this.socket.on('customerTookSnapshot', (data) => {
            if (!this.isCurrentSession(data)) return;
            message.info('客户已拍照，正在归档');
        });
    };

    disconnectSocket = () => {
        if (this.socket) {
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
        }
    };

    // ============ TRTC：坐席纯音频（不推视频），只拉客户远程视频 + 音频 ============
    autoEnterRoomIfReady = (maybeSession) => {
        const s = maybeSession || this.session;
        const status = (s && s.status) || '';
        // ACCEPTED：坐席已经在后端"answer"了，直接进房；否则不自动进
        if (status !== 'ACCEPTED' && status !== 'IN_ROOM' && status !== 'CONNECTED') return;
        if (this.trtc || this._entering) return; // 已进房或正在进房，防止并发
        this._entering = true;
        this.enterTrtcRoom(s).catch((err) => {
            this.safeSetState({ error: err.message || '进入视频房间失败' });
            message.error(err.message || '进入视频房间失败');
        }).finally(() => {
            this._entering = false;
        });
    };

    enterTrtcRoom = async (s) => {
        let session = s;
        if (!session || !session.sdkAppId || !session.agentUserId || !session.agentUserSig) {
            const res = await joinVideoClaim(this.sessionId);
            session = (res && res.result) || res;
        }
        const sdkAppId = session.sdkAppId;
        const userId = session.agentUserId;
        const userSig = session.agentUserSig;
        const roomId = Number(session.roomId);
        if (!sdkAppId || !userId || !userSig || !roomId) {
            throw new Error('进入视频房间的凭据不全');
        }
        this.cleanupTrtc();
        this.sdk = await loadTrtcSdk();
        const support = await this.sdk.isSupported();
        if (!support || !support.result) throw new Error('当前浏览器不支持视频通话，请使用最新版 Chrome/Edge/Safari');
        const trtc = this.sdk.create();
        this.trtc = trtc;
        // 客户进房时打印日志，便于排查是否同房间
        trtc.on(this.sdk.EVENT.REMOTE_USER_ENTER, ({ userId: ruId }) => {
            console.log('[VideoSurvey] 远端用户进房:', ruId);
        });
        // 客户音频可用 → 确认音视频通道已建立
        trtc.on(this.sdk.EVENT.REMOTE_AUDIO_AVAILABLE, ({ userId: ruId }) => {
            console.log('[VideoSurvey] 远端音频可用:', ruId);
        });
        // 客户视频可用时，拉到 <video> 显示
        trtc.on(this.sdk.EVENT.REMOTE_VIDEO_AVAILABLE, ({ userId: ruId, streamType }) => {
            console.log('[VideoSurvey] 远端视频可用:', ruId, 'streamType:', streamType);
            trtc.startRemoteVideo({ userId: ruId, streamType, view: 'video-survey-remote' })
                .then(() => {
                    console.log('[VideoSurvey] startRemoteVideo 成功');
                    this.safeSetState({ remoteReady: true });
                    this.startAspectPoll();
                })
                .catch((err) => {
                    console.error('[VideoSurvey] startRemoteVideo 失败:', err);
                    this.safeSetState({ error: '播放客户视频失败' });
                });
        });
        trtc.on(this.sdk.EVENT.REMOTE_VIDEO_UNAVAILABLE, () => {
            this.clearAspectPoll();
            this.safeSetState({ remoteReady: false });
        });
        // TRTC 错误事件
        trtc.on(this.sdk.EVENT.ERROR, (error) => {
            console.error('[VideoSurvey] TRTC 错误:', error);
        });
        // TRTC v5 用 CONNECTION_STATE_CHANGED 替代 v4 的 ENTER_ROOM / EXIT_ROOM
        trtc.on(this.sdk.EVENT.CONNECTION_STATE_CHANGED, ({ state }) => {
            console.log('[VideoSurvey] 连接状态变化:', state);
            if (state === 'CONNECTED') {
                this.safeSetState({ connected: true, durationSec: 0 });
                this.startDurationTimer();
            } else if (state === 'DISCONNECTED') {
                this.clearDurationTimer();
                this.safeSetState({ connected: false, remoteReady: false });
            }
        });

        // 坐席端：只推本地音频（不推本地视频），但需要接收客户视频
        await trtc.enterRoom({
            sdkAppId,
            userId,
            userSig,
            roomId,
            scene: this.sdk.TYPE.SCENE_RTC,
            autoReceiveAudio: true,
            autoReceiveVideo: true,  // 关键：自动订阅客户视频流
            enableAutoPlayDialog: true,
        });
        // 720P 客户视频拉取（非强制；由 TRTC 自身自适应）
        try {
            await trtc.setVideoProfile({ profile: '720p' }, this.sdk.TYPE.VIDEO_STREAM_REMOTE);
        } catch (e) {}
        try {
            // 音频优先：QoS 退化时优先保音频
            await trtc.setNetworkQosParam({
                prefer: this.sdk.TYPE.QOS_PREFERENCE.CLEAR_AUDIO,
            });
        } catch (e) {}
        try {
            await trtc.startLocalAudio();
        } catch (e) {
            // 某些设备麦克风授权失败时也允许继续（至少看得到客户）
            message.warning('本地麦克风未授权，您可能无法向客户说话');
        }
    };

    cleanupTrtc = () => {
        this._entering = false; // 重置进房标志，允许后续重新进房
        this.clearAspectPoll();
        const safely = (work) => Promise.resolve().then(work).catch(() => undefined);
        if (this.trtc) {
            const trtc = this.trtc;
            this.trtc = null;
            safely(() => trtc.stopLocalAudio());
            safely(() => trtc.stopLocalVideo());
            safely(() => trtc.exitRoom());
            safely(() => trtc.destroy && trtc.destroy());
        }
        this.clearDurationTimer();
        if (this.state.remoteReady || this.state.connected || this.state.durationSec || this.state.aspectRatio) {
            this.safeSetState({ remoteReady: false, connected: false, durationSec: 0, aspectRatio: null });
        }
    };

    // ============ 操作：拍照 / 结束 ============
    /**
     * 坐席端拍照：Canvas 截取远程 <video> 帧 → JPEG base64 → 上传后端（picture 表，本地类目）
     * 因为是 TRTC 入门版，不能使用 SDK 自带的 snapshotTrtcVideoFrame。
     */
    takeSnapshot = async () => {
        if (this.state.snapshotting) return;
        const videoEl = document.getElementById('video-survey-remote');
        // TRTC 的 view 参数实际会把 <video> 插到指定容器里；需要找到其中的 video 元素
        let videoNode = null;
        if (!videoNode && videoEl && videoEl.querySelectorAll) {
            videoNode = videoEl.querySelector('video');
        }
        if (!videoNode) {
            videoNode = document.querySelector('video#video-survey-remote');
        }
        if (!videoNode) {
            // 兜底：所有 video 中找到"已经有 srcObject"且在 remote 容器下的
            const all = document.querySelectorAll('video');
            for (let i = 0; i < all.length; i++) {
                const v = all[i];
                const p = v.parentNode && v.parentNode.id === 'video-survey-remote';
                const pp = v.parentNode && v.parentNode.parentNode && v.parentNode.parentNode.id === 'video-survey-remote';
                if ((p || pp) && v.srcObject) { videoNode = v; break; }
            }
        }
        if (!videoNode) {
            message.warning('暂无客户视频画面，无法截图');
            return;
        }
        if (!videoNode.videoWidth || !videoNode.videoHeight) {
            message.warning('客户视频尚未就绪，无法截图');
            return;
        }
        this.safeSetState({ snapshotting: true });
        try {
            const canvas = document.createElement('canvas');
            canvas.width = videoNode.videoWidth;
            canvas.height = videoNode.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(videoNode, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
            makeSnapshotThumb(dataUrl);
            const res = await uploadVideoClaimSnapshot({
                sessionId: this.sessionId,
                dataUrl,
                takenBy: 'agent',
                label: '本地',
                lossRef: this.session.inviteeRef || '',
            });
            if (res && res.errCode === 0) {
                message.success('截图已保存');
            } else {
                message.error((res && res.errInfo) || '截图保存失败');
            }
        } catch (e) {
            message.error('截图失败');
        } finally {
            this.safeSetState({ snapshotting: false });
        }
    };

    formatDuration = (sec) => {
        const s = Number(sec) || 0;
        const h = String(Math.floor(s / 3600)).padStart(2, '0');
        const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
        const se = String(s % 60).padStart(2, '0');
        return `${h}:${m}:${se}`;
    };

    end = async () => {
        this.safeSetState({ loading: true });
        try {
            const res = await endVideoClaimSession(this.sessionId);
            // http.post resolve 的是整个 {errCode, result} 对象，真正的会话在 result 里。
            // 解包后回写全局通道，才能让订阅方（工单详情）正确拿到 status=ENDED 并刷新按钮
            const session = (res && res.result) || res;
            this.cleanupTrtc();
            this.props.onSessionChanged && this.props.onSessionChanged(session);
            this.props.onSessionEnded && this.props.onSessionEnded(session);
        } catch (error) {
            message.error(error.message || '结束视频失败');
        } finally {
            this.safeSetState({ loading: false });
        }
    };

    render() {
        const { session = {} } = this.props;
        const { loading, error, remoteReady, connected, durationSec, snapshotting, size, aspectRatio } = this.state;
        const inviteeType = session.inviteeType;
        const typeLabel = INVITEE_TYPE_LABEL[inviteeType] || '客户';
        const name = session.inviteeName || '';
        const flowId = session.flowId || '';
        const status = session.status || '';
        const needWait = status === 'INVITED' || status === 'CALLING';
        const inRoom = status === 'ACCEPTED' || status === 'IN_ROOM' || status === 'CONNECTED' || connected;
        // 非通话中状态不渲染房间 UI：
        //   INVITED（已邀请，客户未点链接）/ CALLING（客户呼叫中，坐席未接听）→ 由全局 VideoClaimIncoming 接听弹窗处理
        //   只有坐席已接听（ACCEPTED/IN_ROOM/CONNECTED）才进入房间
        if (!inRoom) return null;

        const sizeIdx = SIZE_STEPS.indexOf(size);
        // 画面区高度：优先按视频真实宽高比算；读不到时用档位默认竖向高度
        let frameHeight = SIZE_FRAME_HEIGHT[size] || SIZE_FRAME_HEIGHT.m;
        if (aspectRatio && aspectRatio > 0) {
            const frameWidth = SIZE_WIDTH[size] || SIZE_WIDTH.m;
            const h = Math.round(frameWidth / aspectRatio);
            // 限制在合理范围，避免极端横屏/竖屏导致过高或过矮
            frameHeight = Math.min(640, Math.max(120, h));
        }
        return (
            <Draggable handle=".video-survey-room header">
            <section className={`video-survey-room video-claim-room size-${size}`}>
                <header>
                    <strong>视频理赔</strong>
                    <span className="sub">
                        {typeLabel}
                        {name ? <span className="name"> · {name}</span> : null}
                        {flowId ? <span className="flow"> · {flowId}</span> : null}
                    </span>
                    <span className="status">
                        {inRoom ? (connected ? '已接通' : '进入房间中…') : needWait ? '等待客户点击链接并发起呼叫…' : (session.status || '')}
                    </span>
                    {inRoom ? <span className="duration">{this.formatDuration(durationSec)}</span> : null}
                    <span className="zoom-controls">
                        <button className="zoom-btn" type="button" title="缩小" disabled={sizeIdx <= 0} onClick={this.zoomOut}>−</button>
                        <button className="zoom-btn" type="button" title="恢复默认" onClick={this.resetSize}>⟲</button>
                        <button className="zoom-btn" type="button" title="放大" disabled={sizeIdx >= SIZE_STEPS.length - 1} onClick={this.zoomIn}>＋</button>
                    </span>
                </header>
                <div className="video-survey-frames" style={{ height: frameHeight }}>
                    <div id="video-survey-remote" className="remote-frame">
                        {!remoteReady ? (
                            <div className="remote-placeholder">
                                <Spin spinning={loading || inRoom} size="large" />
                                <div className="remote-placeholder-tip">
                                    {inRoom ? '等待客户视频画面…' : '客户尚未发起呼叫'}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
                {error ? <p className="video-survey-error">{error}</p> : null}
                <div className="video-survey-actions">
                    <Button
                        type="primary"
                        loading={snapshotting}
                        disabled={!inRoom}
                        onClick={this.takeSnapshot}
                    >
                        拍照
                    </Button>
                    <Popconfirm
                        title="确定结束本次视频通话？"
                        description="结束后将生成全程录像文件并归档到音视频栏目"
                        onConfirm={this.end}
                        okText="结束"
                        cancelText="取消"
                    >
                        <Button danger loading={loading}>结束通话</Button>
                    </Popconfirm>
                </div>
                {loading ? <Spin className="video-survey-loading" size="small" /> : null}
            </section>
            </Draggable>
        );
    }
}

export default VideoSurveyRoom;
